import { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useLazyGetSwapQuoteQuery } from '../../redux/api/authorized/swap';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../redux/slices/auth';
import { toTokenAmount, useSolana } from '../../hooks/useSolana';
import { useNavigate, useParams } from 'react-router-dom';
import {
  selectSolanaUSDCBalance,
  selectTokenBalance,
} from '../../redux/slices/walletSlice';
import { selectTokenBySymbol } from '../../redux/slices/tokenSlice';
import { Container, ModalContainer } from '../BaseModal';
import { useGetWithdrawalInfoQuery } from '../../redux/api/authorized/withdrawal';
import { toast } from 'react-toastify';
import LoadingSpinner from '../LoadingSpinner';
import { BodyLarge, TitleLarge } from '../Texts';
import { UPDATE_BALANCE } from '../../common/constants';
import Button from '../Button';
import { formatVets } from '../../common/currency';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const Swap = () => {
  const usdcBalance = useSelector(selectSolanaUSDCBalance);
  const [vetsPrice, setVetsPrice] = useState(null);
  const [swaped, setSwaped] = useState(false);
  const [usdAmount, setUsdAmount] = useState(0);
  const [vetsAmount, setVetsAmount] = useState(0);
  const privyToken = useSelector(selectPrivyToken);
  const navigate = useNavigate();

  const theme = useTheme();

  const { type, id } = useParams();

  const { data: withdrawalInfo } = useGetWithdrawalInfoQuery(id, {
    skip: !id || !(type === 'withdraw') || !privyToken,
  });

  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();
  const { sendUsdc, walletsReady, loading, getWalletPendingTransactions } =
    useSolana();

  const [pendingTransactions, setPendingTransactions] = useState([]);

  const vetsToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'VETS'),
  );

  useEffect(() => {
    (async () => {
      setPendingTransactions(await getWalletPendingTransactions());
    })();
  }, []);

  useEffect(() => {
    const executeSendUsdc = async () => {
      if (withdrawalInfo && walletsReady) {
        if (withdrawalInfo.status !== 'started') {
          navigate('/app/wallet');
        }
      }
    };
    executeSendUsdc();
  }, [withdrawalInfo, walletsReady]);

  useEffect(() => {
    const refreshBalances = async () => {
      setTimeout(() => {
        window.opener.postMessage(
          { type: UPDATE_BALANCE },
          import.meta.env.VITE_FE_HOST,
        );
        window.close();
      }, 5000);
    };
    if (type === 'deposit') {
      refreshBalances();
    }
  }, [type]);

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        outputMint: vetsToken?.address,
        amount: 1000000,
      }).unwrap();
      const price = formatVets(resp.quote.outAmount);
      setVetsPrice(price);
    };

    if (privyToken && !type) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken]);

  useEffect(() => {
    if (usdcBalance) {
      setUsdAmount(usdcBalance);
    }
  }, [usdcBalance]);

  useEffect(() => {
    if (!swaped && usdAmount > 0 && vetsPrice) {
      // Calculate VETS amount when USD changes (buy mode)
      const vetsAmount = usdAmount / vetsPrice;
      setVetsAmount(vetsAmount);
    } else if (swaped && vetsAmount > 0 && vetsPrice) {
      // Calculate USD amount when VETS changes (sell mode)
      const usdValue = vetsAmount * vetsPrice;
      setUsdAmount(usdValue);
    }
  }, [usdAmount, vetsAmount, vetsPrice, swaped]);

  if (withdrawalInfo?.status === 'started' && !loading) {
    return (
      <Container>
        <ModalContainer>
          <TitleLarge>Confirm withdraw</TitleLarge>
          <BodyLarge style={{ color: theme.text.secondary }}>
            When clicking confirm, you will transfer $
            {withdrawalInfo.outputAmount} USDC to Coinbase and complete the
            withdraw process.
          </BodyLarge>
          <Button
            disabled={pendingTransactions?.length !== 0}
            onClick={async () => {
              if (withdrawalInfo && walletsReady) {
                const resp = await sendUsdc(
                  toTokenAmount(withdrawalInfo.outputAmount, 6),
                  withdrawalInfo.addressTo,
                );
                if (resp?.success) {
                  if (window.opener) {
                    window.opener.postMessage(
                      { type: UPDATE_BALANCE },
                      import.meta.env.VITE_FE_HOST,
                    );
                    window.close();
                  } else {
                    navigate('/');
                  }
                } else {
                  toast('Withdrawal failed', { type: 'error' });
                }
              }
            }}
          >
            Confirm
          </Button>
        </ModalContainer>
      </Container>
    );
  }

  return (
    <Container>
      <LoadingContainer>
        <LoadingSpinner />
        <BodyLarge>Completing {type}...</BodyLarge>
      </LoadingContainer>
    </Container>
  );
};

export default Swap;
